import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import Layout from "containers/LayoutV2";
import UserApi from "service/userApi";
import KhachHangGiaTangApi from "service/BaoCao/KhachHangGiaTangApi";
import { pushNotification } from "utils/notifications";
import { SearchBarKhachHangGiaTang } from "./SearchBarKhachHangGiaTang";
import { SummaryKhachHangGiaTang } from "./SummaryKhachHangGiaTang";
import { BieuDoTronKhachHang } from "./BieuDoTronKhachHang";
import { layHaiKy, KY_LIEN_TRUOC } from "./khoangThoiGian";
import { ghepDuLieuSoSanh } from "./chuanHoaDuLieu";

const BO_LOC_MAC_DINH = {
  ky: "thangnay",
  tuNgay: null,
  denNgay: null,
  kySoSanh: KY_LIEN_TRUOC,
  ssTuNgay: null,
  ssDenNgay: null,
};

/** Mỗi biểu đồ là 1 card, 3 card trên 1 dòng, thu hẹp còn 2 rồi 1 trên màn hình nhỏ */
const customStyles = `
  .khgt-luoi-bieu-do {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 10px;
      align-items: stretch;
  }
  .khgt-the-bieu-do {
      min-width: 0;
      margin-bottom: 0 !important;
      height: 100%;
  }
  .khgt-the-bieu-do .card-body {
      height: 100%;
  }
  @media (max-width: 1199px) {
      .khgt-luoi-bieu-do { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 767px) {
      .khgt-luoi-bieu-do { grid-template-columns: minmax(0, 1fr); }
  }
`;

/** Các nhóm biểu đồ tròn hiển thị trên màn hình */
const DANH_SACH_BIEU_DO = [
  { khoa: "TheoDoTuoi", tieuDe: "Khách hàng gia tăng theo độ tuổi" },
  { khoa: "TheoNgheNghiep", tieuDe: "Khách hàng gia tăng theo nghề nghiệp" },
  { khoa: "TheoKhuVuc", tieuDe: "Khách hàng gia tăng theo khu vực" },
  { khoa: "TheoHinhThucVay", tieuDe: "Khách hàng gia tăng theo hình thức vay" },
  { khoa: "TheoLoaiTaiSan", tieuDe: "Khách hàng gia tăng theo loại tài sản" },
  {
    khoa: "TheoLoaiKhachHang",
    tieuDe: "Khách hàng gia tăng theo loại khách hàng",
  },
];

export const ThongKeKhachHangGiaTang = () => {
  const [boLoc, setBoLoc] = useState(BO_LOC_MAC_DINH);
  const [loading, setLoading] = useState(true);
  const [duLieu, setDuLieu] = useState({ kyNay: null, kyTruoc: null });
  const [hasPermission, setHasPermission] = useState(null);

  const { kyNay, kyTruoc } = useMemo(() => layHaiKy(boLoc), [boLoc]);

  const fetchPermission = () => {
    UserApi.getAllQuyen()
      .then((resQuyen) => {
        const detailedRoles = resQuyen.data.map((item) =>
          item.ModuleCode.toLowerCase()
        );
        setHasPermission(
          detailedRoles.includes("khachhanggiatang") ||
            detailedRoles.includes("quanlykhachhang")
        );
      })
      .catch((err) => {
        console.error("Lỗi load quyền chi tiết:", err);
        setHasPermission(false);
      });
  };

  const fetchData = useCallback(() => {
    if (!kyNay) {
      setDuLieu({ kyNay: null, kyTruoc: null });
      setLoading(false);
      return;
    }
    setLoading(true);

    KhachHangGiaTangApi.getThongKe(
      format(kyNay.from, "dd/MM/yyyy"),
      format(kyNay.to, "dd/MM/yyyy"),
      kyTruoc ? format(kyTruoc.from, "dd/MM/yyyy") : null,
      kyTruoc ? format(kyTruoc.to, "dd/MM/yyyy") : null
    )
      .then((res) => {
        if (res.status === 200 && res.data) {
          setDuLieu({
            kyNay: res.data.KyNay || null,
            kyTruoc: res.data.KyTruoc || null,
          });
        } else {
          setDuLieu({ kyNay: null, kyTruoc: null });
        }
      })
      .catch((err) => {
        console.error(err);
        pushNotification("Tải dữ liệu báo cáo thất bại!", "error");
        setDuLieu({ kyNay: null, kyTruoc: null });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [kyNay, kyTruoc]);

  useEffect(() => {
    document.title =
      "Báo cáo khách hàng gia tăng | Phần mềm quản lý cầm đồ Morman";
    fetchPermission();
  }, []);

  useEffect(() => {
    if (hasPermission) {
      fetchData();
    }
  }, [hasPermission, fetchData]);

  const tongKyNay = (duLieu.kyNay && duLieu.kyNay.TongKhachHangMoi) || 0;
  const tongKyTruoc = (duLieu.kyTruoc && duLieu.kyTruoc.TongKhachHangMoi) || 0;

  if (hasPermission === false) {
    return (
      <Layout>
        <h2 className="text-danger">
          <div className="custom-table-row">
            <div
              className="custom-table-cell text-center red"
              style={{ width: "100%" }}
            >
              Bạn không có quyền truy cập trang này!
            </div>
          </div>
        </h2>
      </Layout>
    );
  }

  return (
    <Layout>
      <div id="form-repeater" className="p-2">
        <style>{customStyles}</style>
        <div className="row header justify-content-start mb-1 ml-1">
          <img src="/app-assets/images/icon-thong-ke.png" alt="Icon" />
          <h1 className="text-info-home">Báo Cáo Khách Hàng Gia Tăng</h1>
        </div>

        <div className="card border-light mb-1 card-v2">
          <div className="card-body card-body-v2">
            <SearchBarKhachHangGiaTang
              boLocMacDinh={BO_LOC_MAC_DINH}
              onApply={setBoLoc}
            />
          </div>
        </div>

        <div className="card border-light mb-1 card-v2">
          <div className="card-body card-body-v2">
            <SummaryKhachHangGiaTang
              tongKyNay={tongKyNay}
              tongKyTruoc={tongKyTruoc}
              kyNay={kyNay}
              kyTruoc={kyTruoc}
              loading={loading}
            />
          </div>
        </div>

        <div
          style={{ fontSize: 12, color: "#858796", padding: "0 4px 6px" }}
        >
          (*) Khách hàng mới được tính theo ngày tạo khách hàng; hình thức vay và
          loại tài sản lấy theo hợp đồng đầu tiên (ngày vay sớm nhất) của khách.
          Khách chưa khai thông tin sẽ nằm ở nhóm "Chưa xác định".
        </div>

        <div className="khgt-luoi-bieu-do">
          {DANH_SACH_BIEU_DO.map((bieuDo) => (
            <div
              key={bieuDo.khoa}
              className="card border-light mb-1 card-v2 khgt-the-bieu-do"
            >
              <div className="card-body card-body-v2">
                <BieuDoTronKhachHang
                  tieuDe={bieuDo.tieuDe}
                  duLieu={ghepDuLieuSoSanh(
                    duLieu.kyNay && duLieu.kyNay[bieuDo.khoa],
                    duLieu.kyTruoc && duLieu.kyTruoc[bieuDo.khoa]
                  )}
                  nhanKyNay={(kyNay && kyNay.nhan) || "Kỳ này"}
                  nhanKyTruoc={(kyTruoc && kyTruoc.nhan) || "Kỳ trước"}
                  loading={loading}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};
